import { useRef, useState } from 'react'
import { processFile } from '../../../data/DataMapper/extra/useDataMapper'
import { DataBridgeImportProps } from './../extra/types'
import _set from 'lodash/set'
import { convertTableToObject, getFileMeta, readJsonFile, convertJsonToTableFormat } from '../extra/helpers'
import { getMessage } from '../../../../common/utils/MessageUtils'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'

/**
 * @typedef {import('zod/mini').infer<typeof DataBridgeImportProps>} Type
 * @type {React.FC<Type>} DataBridgeImport
 */
export const DataBridgeImport = (_props) => {
  const { text = 'インポート', onImport, onError, className, disabled, ...props } = DataBridgeImportProps.parse(_props)

  const [isLoading, setLoading] = useState(false)
  const fileInputRef = useRef()

  /** @function openFileSelector ファイル選択を開く */
  function openFileSelector() {
    if (!fileInputRef.current) return
    fileInputRef.current.click()
  }

  /**
   * @function handleFileChange
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  async function handleFileChange(e) {
    setLoading(true)
    if (!e.target.files) {
      setLoading(false)
      return
    }

    try {
      const file = e.target.files?.[0]

      if (!file) throw new Error('ファイルがアップロードされていません')

      const fileMeta = getFileMeta(file)
      let results

      // JSONファイルの場合は特別処理
      if (fileMeta.type === 'json') {
        const jsonData = await readJsonFile(file)
        const tableFormat = convertJsonToTableFormat(jsonData)
        results = [
          {
            SheetName: 0,
            Headers: tableFormat.Headers,
            RecordCount: tableFormat.Records.length,
            Records: tableFormat.Records
          }
        ]
      } else {
        // CSV/Excelの場合は既存の処理
        results = await processFile(file)
      }

      const sheets = await Promise.all(
        results.map(async (r) => ({
          records: convertTableToObject([r.Headers, ...r.Records]),
          sheetName: String(r?.SheetName || 0)
        }))
      )

      const sheetList = sheets.map((s) => String(s.sheetName))

      onImport(fileMeta, sheets, sheetList)

      e.target.value = ''
    } catch (error) {
      // ファイル解析失敗の英語エラーを伝播させず E011023 を渡す（バグ #3228）
      console.error(error)
      onError?.(getMessage('E011023'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <input
        data-testid={'dataImportFileInput'}
        ref={fileInputRef}
        className="hidden"
        type="file"
        name="dataImport"
        accept=".csv,.xlsx,.json"
        onChange={handleFileChange}
      />
      <button
        disabled={disabled}
        data-testid={props?.['data-testid']}
        type="button"
        className={
          `rounded-lg bg-white border-[#383947] border h-[45px] items-center` +
          ` text-sm text-[#383947b3] px-4 py-2 inline-flex gap-2` +
          ` ${!disabled && 'hover:text-[#383947] hover:font-medium hover:border-1.5 hover:border-[#383947]'}  ` +
          ` ${disabled && 'bg-gray-300/40 cursor-not-allowed '} ${className}`
        }
        onClick={openFileSelector}
      >
        <CloudUploadIcon fontSize="small" />
        {!isLoading ? text : <>読み込み中...</>}
      </button>
    </>
  )
}
